import type { FormEvent, ReactNode } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { Box } from '../box/box';
import { Label } from '../label/label';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';

import * as styles from './form-control.css';

export type FormControlProps = {
  /**
   * unlike other elements where we use the name for the id, in this case
   * the component is mostly used in a group, so the id needs to be unique
   * but the name is for the group.
   */
  id: string;
  onChange?: (value: FormEvent<HTMLInputElement>) => void;
  label: ReactNode;
  defaultChecked?: boolean;
  'data-testid'?: string;
  children?: ReactNode;
  disabled?: boolean;
  name: string;
  screenReaderOnlyLabel?: boolean;
} & (
  | {
      type: 'radio';
      /** a string value required to when reading the form on submit*/
      value: string;
    }
  | { type: 'checkbox'; value?: string }
);

const TEST_IDS = {
  INPUT: 'form-control',
  FAUX_INPUT: 'form-control-faux-input',
};

export const FormControl = ({
  onChange,
  id,
  label,
  defaultChecked = false,
  'data-testid': testId,
  type,
  children,
  disabled = false,
  name,
  value,
  screenReaderOnlyLabel = false,
  ...props
}: FormControlProps) => (
  <Box
    stack="row"
    alignContent={{ x: 'left', y: 'top' }}
    spacing="baseNeg2"
    touchArea={!disabled}
    data-testid={testId}
  >
    <Box
      Component="label"
      variant="grid"
      className={classFilter([
        styles.fauxInput,
        type === 'radio' && styles.radio,
        !disabled && styles.enabled,
      ])}
      color={disabled ? 'neutral250' : 'neutral500'}
      rounded={type === 'checkbox'}
      border="currentColor"
      size={{ minWidth: 'basePos3', minHeight: 'basePos3' }}
      alignContent={{ x: 'center', y: 'center' }}
      data-testid={TEST_IDS.FAUX_INPUT}
    >
      <span className={styles.checked}>{children}</span>

      <input
        id={id}
        name={name}
        type={type}
        defaultChecked={defaultChecked}
        data-default-checked={defaultChecked}
        onChange={onChange}
        data-testid={TEST_IDS.INPUT}
        disabled={disabled}
        value={value}
        className={classFilter([
          styles.input,
          type === 'radio' && styles.radio,
        ])}
        {...props}
      />
    </Box>
    <Label htmlFor={id || name} disabled={disabled} variant="body150">
      {screenReaderOnlyLabel ? (
        <ScreenReaderText>{label}</ScreenReaderText>
      ) : (
        label
      )}
    </Label>
  </Box>
);
